import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import ResponsiveDialog from '../common/ResponsiveDialog';
import pettyCashApi from '../../services/pettyCashApi';
import { showSnackbar } from '../../store/slices/uiSlice';
import type { ConfirmationList, ConfirmationRow, ConfirmationState } from '../../types/pettyCash';
import { formatAmount, formatDate, formatDateTime, openQuarter, quarterLabel, recentQuarters, signedAmount, todayIso } from '../../utils/pettyCash';

/**
 * Petty Cash Setup › Confirmations: per quarter, every holder with a float and his confirmation of the cash in his
 * custody (or that it is missing), with the expected cash and the difference; finance marks each one reviewed.
 */

const STATE: Record<ConfirmationState, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  Confirmed: { label: 'Confirmed', color: 'success' },
  Difference: { label: 'Difference', color: 'warning' },
  Missing: { label: 'Missing', color: 'error' },
  Reviewed: { label: 'Reviewed', color: 'info' },
};

const shown = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatAmount(n));

interface Props {
  companyId: number;
  currency: string;
  /** The confirmation window of the settings: right after a quarter end, the previous quarter is the one due. */
  windowDays: number;
}

const ConfirmationsTab: React.FC<Props> = ({ companyId, currency, windowDays }) => {
  const dispatch = useDispatch();
  // The current quarter and the 7 before it; it opens on the quarter whose confirmation is due.
  const quarters = useMemo(() => recentQuarters(todayIso(), 8), []);
  const [quarter, setQuarter] = useState(() => openQuarter(todayIso(), windowDays) || quarters[0] || '');
  const [list, setList] = useState<ConfirmationList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The dialog keeps its row while it closes.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewing, setReviewing] = useState<ConfirmationRow | null>(null);
  const [note, setNote] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setList(await pettyCashApi.confirmations(companyId, quarter));
      setError(null);
    } catch (e: any) {
      setList(null);
      setError(e?.response?.data?.message || 'The confirmations could not be loaded');
    } finally {
      setLoading(false);
    }
  }, [companyId, quarter]);

  useEffect(() => { void load(); }, [load]);

  const review = async () => {
    const c = reviewing?.confirmation;
    if (!c) return;
    setReviewBusy(true);
    setReviewError(null);
    try {
      await pettyCashApi.reviewConfirmation(c.confirmationId, note.trim() || null);
      setReviewOpen(false);
      dispatch(showSnackbar({ message: 'Confirmation marked reviewed', severity: 'success' }));
      await load();
    } catch (e: any) {
      setReviewError(e?.response?.data?.message || e?.message || 'The review could not be saved');
    } finally {
      setReviewBusy(false);
    }
  };

  const counts = (list?.rows || []).reduce<Record<string, number>>((m, r) => ({ ...m, [r.state]: (m[r.state] || 0) + 1 }), {});
  const columns: ResponsiveTableColumn<ConfirmationRow>[] = [
    { id: 'holder', header: 'Holder', cell: (r) => <Typography variant="body2" fontWeight={600}>{r.holderName}</Typography> },
    {
      id: 'state',
      header: 'State',
      cell: (r) => {
        const s = STATE[r.state] || { label: r.state, color: 'info' as const };
        return <Chip size="small" color={s.color} variant={r.state === 'Missing' ? 'outlined' : 'filled'} label={s.label} />;
      },
    },
    { id: 'float', header: `Float ${currency}`.trim(), align: 'right', cell: (r) => shown(r.confirmation?.floatAmount ?? r.floatAmount) },
    {
      id: 'deducted',
      header: 'Not yet reimbursed / advances',
      align: 'right',
      hideOnMobile: true,
      cell: (r) => (r.confirmation ? `${shown(r.confirmation.outstanding)} / ${shown(r.confirmation.advances)}` : '—'),
    },
    {
      id: 'cash',
      header: 'Cash in hand / expected',
      align: 'right',
      cell: (r) => (r.confirmation ? `${shown(r.confirmation.cashInHand)} / ${shown(r.confirmation.expectedCash)}` : '—'),
    },
    {
      id: 'difference',
      header: 'Difference',
      align: 'right',
      cell: (r) => (r.confirmation?.difference !== null && r.confirmation?.difference !== undefined ? signedAmount(r.confirmation.difference) : '—'),
    },
    {
      id: 'confirmed',
      header: 'Confirmed',
      cell: (r) => (r.confirmation ? (
        <Box>
          <Typography variant="body2">{formatDateTime(r.confirmation.confirmedAt)}</Typography>
          {r.confirmation.note ? <Typography variant="caption" color="text.secondary">{r.confirmation.note}</Typography> : null}
        </Box>
      ) : '—'),
    },
    {
      id: 'review',
      header: 'Review',
      cell: (r) => {
        const c = r.confirmation;
        if (c?.reviewedAt) {
          return (
            <Box>
              <Typography variant="body2">{c.reviewedByName || '—'} · {formatDate(c.reviewedAt)}</Typography>
              {c.reviewNote ? <Typography variant="caption" color="text.secondary">{c.reviewNote}</Typography> : null}
            </Box>
          );
        }
        return c && list?.canReview ? (
          <Button size="small" variant="outlined" onClick={() => { setReviewing(r); setReviewOpen(true); setNote(''); setReviewError(null); }}>Mark reviewed</Button>
        ) : '—';
      },
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <TextField id="pc-confirm-quarter" select size="small" label="Quarter" value={quarter} onChange={(e) => setQuarter(e.target.value)} sx={{ minWidth: 220 }}>
          {quarters.map((q) => <MenuItem key={q} value={q}>{quarterLabel(q)}</MenuItem>)}
        </TextField>
        {list ? (
          <Typography variant="body2" color="text.secondary">
            Due from {formatDate(list.windowStart)} to {formatDate(list.windowEnd)}
            {' · '}{counts.Confirmed || 0} confirmed, {counts.Difference || 0} with a difference, {counts.Missing || 0} missing, {counts.Reviewed || 0} reviewed
          </Typography>
        ) : null}
        {loading ? <CircularProgress size={18} /> : null}
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      {list ? (
        <ResponsiveTable
          ariaLabel={`Petty cash confirmations ${quarterLabel(list.quarter)}`}
          columns={columns}
          rows={list.rows}
          getRowKey={(r) => r.holderId}
          emptyMessage="No holder with a float"
        />
      ) : null}

      <ResponsiveDialog open={reviewOpen} onClose={reviewBusy ? undefined : () => setReviewOpen(false)} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby="pc-review-title">
        <DialogTitle id="pc-review-title">Mark reviewed</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {reviewing?.confirmation ? (
              <Typography variant="body2">
                {reviewing.holderName}, {quarterLabel(reviewing.confirmation.quarter)}: cash in hand {formatAmount(reviewing.confirmation.cashInHand)}
                {reviewing.confirmation.difference !== null ? ` · difference ${signedAmount(reviewing.confirmation.difference)}` : ''}
              </Typography>
            ) : null}
            <TextField
              id="pc-review-note"
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 1000))}
              helperText={reviewing?.state === 'Difference' ? 'What was found about the difference' : 'Optional'}
              multiline
              minRows={2}
              fullWidth
            />
            {reviewError ? <Alert severity="error">{reviewError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReviewOpen(false)} disabled={reviewBusy}>Back</Button>
          <Button onClick={() => { void review(); }} disabled={reviewBusy}>{reviewBusy ? 'Saving…' : 'Mark reviewed'}</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default ConfirmationsTab;
